// ExoTest Theme Utilities
// All theme colors are defined in App.css using CSS variables.
// This file owns theme switching and a few helpers for reading the CSS vars in JS.

export type ThemeName = 'light' | 'neon-grid' | 'midnight';

export const themes: { name: ThemeName; label: string; description: string }[] = [
  { name: 'light',      label: 'Light',      description: 'Clean light mode with blue accents' },
  { name: 'neon-grid',  label: 'Neon Grid',  description: 'Cyan glow on deep black circuit grid' },
  { name: 'midnight',   label: 'Midnight',   description: 'Purple/violet on dark blue' },
];

const STORAGE_KEY = 'xotest-theme';

const isThemeName = (value: string | null): value is ThemeName => {
  return value !== null && themes.some(t => t.name === value);
};

// Get the current theme from <html data-theme="...">, defaulting to "light"
export const getCurrentTheme = (): ThemeName => {
  const attr = document.documentElement.getAttribute('data-theme');
  if (isThemeName(attr)) return attr;
  return 'light';
};

// Set the theme on <html> and persist to localStorage
export const setTheme = (theme: ThemeName): void => {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem(STORAGE_KEY, theme);
};

// Initialize theme from localStorage or default to light
export const initTheme = (): void => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (isThemeName(saved)) {
    setTheme(saved);
  } else {
    setTheme('light');
  }
};

// Read a CSS variable value (e.g. getCSSVar('--success'))
export const getCSSVar = (varName: string): string => {
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
};

// Helper: pick a status color based on percentage thresholds
export const getStatusColor = (
  value: number,
  thresholds = { high: 80, medium: 50 }
): string => {
  if (value >= thresholds.high) return getCSSVar('--success');
  if (value >= thresholds.medium) return getCSSVar('--warning');
  return getCSSVar('--danger');
};

// Helper: bug rate color (higher is worse)
export const getBugRateColor = (rate: number): string => {
  if (rate >= 30) return getCSSVar('--danger');
  if (rate >= 15) return getCSSVar('--warning');
  return getCSSVar('--success');
};

// Legacy theme object for backwards compatibility with existing code
// Prefer using CSS classes or getCSSVar directly when possible.
export const theme = {
  // Surfaces
  get void() { return getCSSVar('--void'); },
  get gridDark() { return getCSSVar('--grid-dark'); },
  get gridLine() { return getCSSVar('--grid-line'); },
  get surface() { return getCSSVar('--surface'); },
  get surfaceAlt() { return getCSSVar('--surface-alt'); },
  get surfaceElevated() { return getCSSVar('--surface-elevated'); },

  // Primary / secondary
  get primary() { return getCSSVar('--primary'); },
  get primaryBright() { return getCSSVar('--primary-bright'); },
  get primaryDim() { return getCSSVar('--primary-dim'); },
  get secondary() { return getCSSVar('--secondary'); },
  get secondaryDim() { return getCSSVar('--secondary-dim'); },

  // Legacy "cyan"/"magenta" aliases
  get cyanGlow() { return getCSSVar('--primary'); },
  get cyanBright() { return getCSSVar('--primary-bright'); },
  get cyanDim() { return getCSSVar('--primary-dim'); },
  get magentaGlow() { return getCSSVar('--secondary'); },
  get magentaDim() { return getCSSVar('--secondary-dim'); },

  // Text
  get textPrimary() { return getCSSVar('--text-primary'); },
  get textSecondary() { return getCSSVar('--text-secondary'); },
  get textMuted() { return getCSSVar('--text-muted'); },

  // Status
  get success() { return getCSSVar('--success'); },
  get warning() { return getCSSVar('--warning'); },
  get danger() { return getCSSVar('--danger'); },

  // Shadows / glows
  get borderGlow() { return getCSSVar('--border-glow'); },
  get borderGlowStrong() { return getCSSVar('--border-glow-strong'); },
  get glowSuccess() { return getCSSVar('--glow-success'); },
  get glowDanger() { return getCSSVar('--glow-danger'); },
  get glowWarning() { return getCSSVar('--glow-warning'); },

  // Transparent variants
  get cyanTransparent() { return getCSSVar('--primary-transparent'); },
  get cyanTransparentLight() { return getCSSVar('--primary-transparent-light'); },
  get cyanTransparentStrong() { return getCSSVar('--primary-transparent-strong'); },
  get dangerTransparent() { return getCSSVar('--danger-transparent'); },
  get successTransparent() { return getCSSVar('--success-transparent'); },
  get warningTransparent() { return getCSSVar('--warning-transparent'); },
};

export default theme;
